import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { geocodeApi, type GeocodeResult } from '@/api/endpoints';
import { Input, Spinner } from '@/components/ui';
import { usePopoverPlacement } from '@/hooks/usePopoverPlacement';

interface Props {
  value: string;
  onChange: (address: string) => void;
  /** 选中某条结果时把坐标一并交出去 */
  onPick: (result: GeocodeResult) => void;
  id?: string;
  disabled?: boolean;
}

/**
 * 地址输入 + 自动补全。
 *
 * 管理员手里通常只有一个地址，没有经纬度。让他去地图上找、或者打开别的网站
 * 查坐标再抄回来，既慢又容易抄错一位 —— 而经纬度错一位，工人就在几公里外
 * 打不上卡，还得等到发工资那天才发现。
 *
 * 搜索走后端代理（服务端排队、缓存、带合规的 User-Agent），细节见
 * `apps/api/src/geocode/geocode.service.ts`。
 *
 * **搜不出来不算错。** 工地常常在没编入地图的路段上（新开发区、临时门牌），
 * 这时候手动填坐标仍然是主路径，这个组件只是加速常见情况。
 */
export function AddressSearch({ value, onChange, onPick, id, disabled }: Props) {
  const { t } = useTranslation();
  const [results, setResults] = useState<GeocodeResult[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  /** 刚刚是被「选中某条结果」改的值，别再拿它去搜一次 */
  const skipNextRef = useRef(false);
  /**
   * 人自己敲过字才搜（CR-082）：工地设置打开就是表单、地址框里带着已有的地址，
   * 不挡的话一进页面就弹出一串候选盖住下面的地图。反查回来填上的地址也不该再搜一遍
   */
  const typedRef = useRef(false);

  // 候选往哪开、多高按可视区算（最多 18rem）：输入框在屏幕下半截、手机键盘弹出来时往上开
  usePopoverPlacement(open && results.length > 0, wrapRef, listRef, { maxHeight: 288, contentKey: results, matchWidth: true });

  // 点到别处就收起下拉
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, []);

  useEffect(() => {
    if (skipNextRef.current) {
      skipNextRef.current = false;
      return;
    }
    if (!typedRef.current) return;
    const query = value.trim();
    if (query.length < 3) {
      setResults([]);
      setFailed(false);
      return;
    }

    // 敲一个字搜一次会瞬间打爆上游的每秒一次限额，等手停下来再搜
    const timer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const found = await geocodeApi.search(query);
        setResults(found);
        setOpen(found.length > 0);
        setFailed(false);
      } catch {
        // 上游挂了不提示成红色错误：这只是个便利功能，手填坐标照样能建工地
        setResults([]);
        setFailed(true);
      } finally {
        setLoading(false);
      }
    }, 600);

    return () => window.clearTimeout(timer);
    // 依赖里没有界面语言：地址一律返回英文，切中英文不需要重搜一遍
  }, [value]);

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <Input
          id={id}
          value={value}
          disabled={disabled}
          onChange={(e) => {
            typedRef.current = true;
            onChange(e.target.value);
          }}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder={t('site.addressPlaceholder')}
          maxLength={255}
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
        />
        {loading && (
          <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2">
            <Spinner size={16} />
          </span>
        )}
      </div>

      {open && results.length > 0 && (
        <ul
          ref={listRef}
          className="absolute left-0 z-dropdown w-full overflow-y-auto rounded-md border border-rule-strong bg-popover shadow-pop"
        >
          {results.map((result) => (
            <li key={`${result.lat},${result.lng},${result.address}`}>
              <button
                type="button"
                onClick={() => {
                  skipNextRef.current = true;
                  onChange(result.address);
                  onPick(result);
                  setOpen(false);
                }}
                className="block w-full px-3 py-2.5 text-left transition-colors hover:bg-ink/6"
              >
                <span className="block text-body font-medium">{result.label}</span>
                <span className="mt-0.5 block text-caption leading-snug text-ink-3">
                  {result.address}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {failed && <p className="mt-1.5 text-caption text-ink-3">{t('site.geocodeUnavailable')}</p>}
    </div>
  );
}
